import Backbone from 'backbone'
import template from './source-data-panel.hbs'
import sourceDataRowTemplate from 'scoreboard/imports/source-data-row.hbs'
import Porter from 'porter'
import _ from 'lodash'
import packageTemplate from 'scoreboard/imports/column-mapping-package.hbs'
import $ from 'jquery'
import i18n from 'scoreboard/components/i18n'
import formatValue from 'scoreboard/components/format-value'
import escapePreserveLineBreaks from 'scoreboard/components/escape-preserve-line-breaks'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO } = DataType

var SourceDataPanel = Backbone.View.extend({

	className: 'panel panel-with-table',

	events: {
		'click .porter-package .delete': 'onDeleteMapping'
	},

	/**
	 * Renders a panel which allows the user to specify the columns mappings in
	 * the source data using drag and drop.
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Backbone.Model} options.model
	 *
	 * @param {FormView} options.formView
	 *
	 * @param {Function} options.validMappingOptions returns the mapping options
	 * that the user can drag and drop to the appropriate columns. The mappings
	 * should be an array of objects like [ { id, label, multiple } ]. Multiple
	 * indicates whether the same mapping can be used multiple times (e.g.
	 * thresholds)
	 *
	 * @param {Function} [options.markRequiredOptions] callback that sets
	 * 'is-required' class on the required column mappings for the current
	 * settings.
	 *
	 * @param {Boolean} [options.shorten=false] whether we should abbreviate large
	 * values like 12345 to 12.3K
	 *
	 * @param {Integer} [options.headerIndex] index of the header row
	 */
	initialize(options = {}) {
		this.options = options
		this.formView = options.formView
		_.bindAll(this, 'setTableMaxHeight')
	},

	render() {
		this.$el.empty().append(template(this.context()))
		this.renderMappingPorter()
		this.renderTableBody()

		//  #21759 hide column headings for spreadsheets
		if (this.model.isSpreadsheet()) {
			this.$('table tbody tr:first').addClass('is-not-displayed')
		}

		process.nextTick(() => this.setTableMaxHeight())
		$(window).on('resize', this.setTableMaxHeight)

		return this
	},

	context: function() {
		let type = _.get(this.model.attributes, 'type')
		let isStructure = type === 'scorecard-item' || type === 'initiative-item'
		// #31017 prevent blowups if there is no preview available
		let columns = _.get(this.model.attributes, 'preview.columns', [])
		return {
			keys: _.map(columns, 'label') ,
			structure: isStructure
		}
	},

	renderTableBody: function () {
		let html = ''
		const isDatabaseImport = this.model.isDatabase()
		// first row to display column names (keys)
		// for sql imports, the first row should have the date header toggler
		// #31017 prevent blowups if there is no preview available
		let columns = _.get(this.model.attributes, 'preview.columns', [])
		let rows =  _.get(this.model.attributes, 'preview.rows', [])
		let structure = this.context().structure
		let ctx = {
			data: _.map(columns, it => _.escape(it.label)),
			checkbox: false,
			dateHeaderToggle: isDatabaseImport,
			structure
		}

		let ignoreRows = this.model.get('dataSourceMetadata').ignoreRows || []

		html += sourceDataRowTemplate(ctx)

		// #32480 default to not abbreviating numbers on source data import screen
		let shorten = this.options.shorten || false

		_.each(rows, (row, index) => {
			// #27745 display ignored rows with ignored styling (reverts #25327)
			let data = []
			let rowIndex = index
			_.each(columns, (key, index) => {
				let cell = row.cells[index]
				let value = escapePreserveLineBreaks(_.get(cell, 'value', ''))
				let dataType = _.get(cell, 'dataType')
				// #32771 if we are showing transformed data (there will be
				// something populated in the 'originalValue' in this case),
				// don't shift the decimal for percentages
				let moveDecimalForPercent = _.isNil(_.get(cell, 'originalValue'))
				let formatted = formatValue({
					value,
					dataType,
					shorten,
					moveDecimalForPercent
				})
				// #33709 don't use the formatted value in the header row for
				// cells parsed as yes-no dataTypes (use the raw value instead)
				if (dataType === YES_NO && rowIndex === this.options.headerIndex) {
					formatted = _.get(cell, 'rawValue', '')
				}
				data.push(formatted || '')
			})
			let ctx = {
				index: index,
				data,
				checkbox: true,
				structure,
				dateHeaderToggle: !isDatabaseImport && index === 0,
				ignored:_.includes(ignoreRows, index)
			}
			html += sourceDataRowTemplate(ctx)
		})
		this.$('tbody').append(html)
	},

	renderMappingPorter() {
		let usedOptions = []
		let maxNameMappingOrder = 0

		let mappingOptions = this.options.validMappingOptions()
		let columnMappings = this.model.get('columnMappings')
		// For each column (sourceData.keys), populate it's porter-destination
		// with a porter-package matching the currently mapped option (eg "Threshold")
		let columns = _.get(this.model.attributes, 'preview.columns', [])
		_.each(columns, column => {
			let columnKey = column.label
			let nameMappingOrder

			// Look through each of the possible mapping options to find which
			// one (if any) is mapped to the current column
			let selectedOption = mappingOptions.find(option => {
				// Get the option's corresponding attribute on the model
				// Example: the "threshold" option corresponds to the "thresholdColumns"
				// attribute on the model
				let val = columnMappings[modelAttr(option)]

				if (option.multiple) {
					// If the model attribute can be mapped to multiple columns
					// (like "thresholdColumns"), the attribute is an array; try to
					// find the current column key in the array
					let index = _.findIndex(val, col => col == columnKey)
					if (index !== -1) {
						if (option.id === 'name') {
							// If this is a name mapping, we need to add an "order"
							// to it based on its index the the nameColumns array.
							// The user will expect 1-based indexing, so adjust to that
							nameMappingOrder = index + 1

							if (nameMappingOrder > maxNameMappingOrder) {
								maxNameMappingOrder = nameMappingOrder
							}
						}

						return true
					} else {
						// Current column doesn't have the current mapping option
						return false
					}
				} else {
					// If the model attribute can only be mapped to a single column
					// (like "dateColumn"), just see if it matches the current
					// column key
					return val == columnKey
				}
			})

			if (selectedOption) {
				// If the current column is mapped to something, create a porter-package
				// div for it
				let packageContext = _.extend({
					order: nameMappingOrder
				}, selectedOption)

				this.$(`[data-id='${columnKey}'] .porter-destination`)
					.append(packageTemplate(packageContext))

				if (!selectedOption.multiple) {
					// If the mapping option can only be used once, add it to a
					// running list of used options that should be omitted from
					// the "available" option container
					usedOptions.push(selectedOption)
				}
			}
		})

		// Add any unused options to the "available for use" porter-destination
		this.$('.available-options').append(
			_.difference(mappingOptions, usedOptions).map(option => {
				let packageContext = _.cloneDeep(option)
				if (option.id === 'name') {
					packageContext.order = maxNameMappingOrder + 1
				}

				return packageTemplate(packageContext)
			}))

		let accept = ({ source, destination, sourcePackage }) => {
			let availableOptions = this.$('.available-options')[0]

			if (destination.node == availableOptions) {
				// In Connect, dragging a mapping from the header back to the
				// 'available' pool is how you removed a column's mapping. Instead,
				// we just give a delete icon, and don't support dragging back
				// to the pool.
				// See https://github.com/SpiderStrategies/Scoreboard/issues/21422#issuecomment-451451826
				return Porter.Delivery.REJECT
			} else {
				// Move anything that's already in the column header back to
				// the "available" pool of options
				this.porter.packages(destination.id).forEach(p => {
					if (p.dataset.id === 'name') {
						// deleteMapping() has some logic to re-index the "order"
						// used for name/id mappings, so use that if a name/id
						// column mapping is being replaced
						this.deleteMapping(p)
					} else {
						this.porter.move(p, 'available')
					}
				})

				let option = _.find(this.options.validMappingOptions(), o => o.id == sourcePackage.id)
				if (option.multiple && source.id === 'available') {
					// If the package came from the "available" pool of options
					// and it's allowed to be used on multiple columns, just copy
					// the package (so it's still in the "available" pool)
					return Porter.Delivery.COPY
				} else {
					// If we can only have one instance of the mapping, or if
					// we're moving it from one column header to another, then
					// move the package
					return Porter.Delivery.MOVE
				}
			}
		}

		this.porter = new Porter(this.$('.porter-destination').toArray(), { accept })

		this.porter.on('deliver', ({ deliveredPackage, sourcePackage, source, destination }) => {
			if (deliveredPackage.id === 'name') {
				if (source.id === 'available') {
					let newSourceOrder = parseInt(sourcePackage.node.dataset.order) + 1
					sourcePackage.node.dataset.order = newSourceOrder
					$(sourcePackage.node).find('.order').text(newSourceOrder)
				}
				this.trigger('deliver')
			}

			this.formView.change()
		})
	},

	/**
	 * Overflow scrolling should only be within the table content, leaving the drag and drop options always in view. Issue #25502
	 * To manage this, we will:
	 * 1. First check to see if scrollbars are necessary to see all the content
	 * 2. If scrollbars are necessary, get the total vertical scroll amount
	 * 3. Subtract that amount from the panel-content's current height and set the difference as the panel content's max-height
	 *
	 * Note: We can't just use CSS because the height's of the sibling elements change depending on if text and badges are wrapping.
	 */
	setTableMaxHeight: function() {
		let dialogBody = this.$el.closest('.dialog-body')
		let dialogBodyScrollHeight = dialogBody[0].scrollHeight
		let dialogBodyHeight = dialogBody.height()

		if (dialogBodyScrollHeight > dialogBodyHeight) {
			let overflowTotal = dialogBodyScrollHeight - dialogBodyHeight
			let panelContentHeight = this.$('.panel-content').height()
			this.$('.panel-content').css('max-height', panelContentHeight - overflowTotal)
		}
	},

	/**
	 * Deletes the clicked mapping option from the column
	 */
	onDeleteMapping (e) {
		let option = $(e.target).closest('.porter-package')[0]
		this.deleteMapping(option)
		this.formView.change()
		_.invoke(this, 'markRequiredOptions')
	},

	/**
	 * Deletes the given mapping DOM node from the column
	 */
	deleteMapping (deletePackage) {
		let packageValue = deletePackage.dataset.id
		if (this.$(`.available-options .porter-package[data-id=${packageValue}]`).length > 0) {
			// If the deleted mapping already exists in the 'available' pool, just
			// delete the one the user clicked on
			this.porter.delete(deletePackage)
		} else {
			// If the deleted mapping isn't already in the 'available' pool, then
			// move the one the user clicked on back into the 'available' pool
			this.porter.move(deletePackage, 'available')
		}

		if (packageValue === 'name') {
			// If we're deleting a 'name' mapping, we need to decrement the "order"
			// for all mappings that had a higher sort order
			let deletedOrder = parseInt(deletePackage.dataset.order)
			this.$('.porter-package[data-id=name]').toArray().forEach(pkg => {
				let currentOrder = parseInt(pkg.dataset.order)
				if (currentOrder > deletedOrder) {
					pkg.dataset.order = currentOrder - 1
					$(pkg).find('.order').text(currentOrder - 1)
				}
			})
		}
	},

	test() {
		// at least one column should be mapped
		const hasMapping = _.some(this.porter.val(), (columnTypes, columnName) => {
			return columnName != 'available' && columnTypes.length > 0
		})
		if (!hasMapping) {
			return i18n.get('admin.import.mapResults.pleaseMap')
		}
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.porter], 'remove')
		$(window).off('resize', this.setTableMaxHeight)
	}

})

/**
 * Returns the model attribute name that contains the column keys for the given
 * mapping option.
 *
 * For example, passing in the option for "threshold" will return "thresholdColumns",
 * and passing in the option for "date" will return "dateColumn"
 */
function modelAttr (mappingOption) {
	let attr = mappingOption.id + 'Column'
	if (mappingOption.multiple) {
		attr += 's'
	}
	return attr
}

export default SourceDataPanel

SourceDataPanel.__filename__ = 'imports/source-data-panel.js'
